// `/app/withdrawals` (croqui `screens/seller/saques.tsx`). Foco: **o disponível e o
// botão de sacar**.
//
// Esta é a rota-PAI: ela desenha a tela e, por cima, o `RouteDrawer` com o `<Outlet/>` da
// rota `$id` (`spec/24` §1 decisão 5). Deep link em `.../wd_…` monta a lista embaixo e o
// drawer aberto; fechar volta para cá preservando o `search`.
//
// Zenith (WEB-105): `PageHeader`/`RouteDrawer` de `components/app` no lugar do `kit`;
// `WithdrawalFormDrawer` também usa `RouteDrawer` por baixo (mesmo padrão de "popup" do
// canvas para `NovoSaque` e `Detalhe`), mesmo sem ter URL própria.
//
// O `403 mfa_required` do `POST /v1/me/withdrawals` não é tratado aqui: `api/client.ts`
// abre o `StepUpDialog` e reexecuta a chamada (`spec/17` §4.3).

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import {
  balanceQueryOptions,
  type CreateWithdrawalBody,
  createWithdrawal,
  payableKeys,
  pixKeysQueryOptions,
  ratesQueryOptions,
  type Withdrawal,
  withdrawalsQueryOptions,
} from '../api/queries/withdrawals.ts';
import { useMode } from '../app/mode.tsx';
import { ErrorState } from '../components/app/error-state.tsx';
import { PageHeader } from '../components/app/page-header.tsx';
import { RouteDrawer } from '../components/app/route-drawer.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  countersQueryOptions,
  monthStartIsoDate,
  monthTotals,
  monthWithdrawalsQueryOptions,
} from '../features/withdrawals/queries.ts';
import { TAB_IDS, type TabId, tabCount } from '../features/withdrawals/status.ts';
import { WithdrawalFormDrawer } from '../features/withdrawals/withdrawal-form.tsx';
import { WithdrawalsList } from '../features/withdrawals/withdrawals-list.tsx';
import {
  WithdrawalNotices,
  WithdrawalsSummary,
} from '../features/withdrawals/withdrawals-summary.tsx';

const searchSchema = z.object({
  tab: z.enum(TAB_IDS).optional().catch(undefined),
});

/** Mais recente primeiro: é a ordem que a API já devolve e a que a aba juntada precisa. */
function byRequestedAtDesc(a: Withdrawal, b: Withdrawal): number {
  return b.requested_at.localeCompare(a.requested_at);
}

function Withdrawals() {
  const { mode } = useMode();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const tab: TabId = search.tab ?? 'all';
  const [formOpen, setFormOpen] = useState(false);

  const balance = useQuery(balanceQueryOptions(mode));
  const rates = useQuery(ratesQueryOptions(mode));
  const pixKeys = useQuery(pixKeysQueryOptions(mode));
  const counters = useQuery(countersQueryOptions(mode));
  const month = useQuery(monthWithdrawalsQueryOptions(mode, monthStartIsoDate()));

  // "Falhou/recusado" é a única aba que junta dois status: a API filtra por UM `status`
  // (`spec/09` §5), então a segunda consulta só liga nessa aba.
  const primary = useInfiniteQuery(
    withdrawalsQueryOptions(mode, tab === 'all' ? {} : { status: tab }),
  );
  const rejected = useInfiniteQuery({
    ...withdrawalsQueryOptions(mode, { status: 'rejected' }),
    enabled: tab === 'failed',
  });
  const merged = tab === 'failed';

  const rows = [
    ...(primary.data?.pages ?? []).flatMap((page) => page.data),
    ...(merged ? (rejected.data?.pages ?? []).flatMap((page) => page.data) : []),
  ].sort(byRequestedAtDesc);

  const listPending = primary.isPending || (merged && rejected.isPending);
  const hasMore = primary.hasNextPage || (merged && rejected.hasNextPage);
  const isLoadingMore = primary.isFetchingNextPage || (merged && rejected.isFetchingNextPage);

  const keys = payableKeys(pixKeys.data ?? []);
  const available = balance.data?.available ?? 0;
  const feeCents = rates.data?.current.withdraw_fee_cents ?? 0;
  // `undefined` quando a soma do mês não veio: a faixa mostra ausência, não R$ 0,00.
  const totals = month.data === undefined ? undefined : monthTotals(month.data);
  const counts: Partial<Record<TabId, number>> = {};
  for (const id of TAB_IDS) {
    const count = tabCount(counters.data?.withdrawals, id);
    if (count !== undefined) {
      counts[id] = count;
    }
  }

  /**
   * Taxa e chaves são pré-condição da ação, não enfeite: com `GET /v1/me/rates` em erro o
   * `?? 0` mostraria "taxa R$ 0,00" e um total debitado errado, e com `GET /v1/me/pix-keys`
   * em erro a tela diria "cadastre uma chave PIX" para quem tem uma.
   */
  const setupError = rates.error ?? pixKeys.error;
  const ready = !balance.isPending && !rates.isPending && !pixKeys.isPending;
  /**
   * AUTH-05: trava de 24 h depois de recuperar a 2FA perdida (`spec/03` §4.3). O servidor
   * responde `409 withdrawals_locked`; a tela diz o motivo antes de o seller tentar.
   */
  const lockedUntil = balance.data?.withdrawals_locked_until ?? null;
  const canWithdraw = ready && setupError === null && keys.length > 0 && lockedUntil === null;

  const create = useMutation({
    mutationFn: (body: CreateWithdrawalBody) => createWithdrawal(mode, body),
    onSuccess: async (withdrawal) => {
      setFormOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
      await queryClient.invalidateQueries({ queryKey: ['balance'] });
      await queryClient.invalidateQueries({ queryKey: ['counters'] });
      await navigate({ to: '/app/withdrawals/$id', params: { id: withdrawal.id }, search });
    },
  });

  // A rota `$id` é filha desta: se ela casa, o detalhe está aberto por cima da lista.
  const detail = useMatch({ from: '/app/withdrawals/$id', shouldThrow: false });

  return (
    <>
      <PageHeader
        title="Saques"
        description="Peça um saque do saldo disponível e acompanhe o histórico"
      >
        <Button
          disabled={!canWithdraw}
          onClick={() => {
            create.reset();
            setFormOpen(true);
          }}
        >
          Sacar
        </Button>
      </PageHeader>

      <WithdrawalsSummary
        balance={balance.data}
        rates={rates.data?.current}
        totals={totals}
        isLoading={balance.isPending || rates.isPending || month.isPending}
        error={balance.error ?? rates.error}
        onRetry={() => {
          void balance.refetch();
          void rates.refetch();
          void month.refetch();
        }}
      />

      <WithdrawalNotices
        hasPixKey={pixKeys.isSuccess ? keys.length > 0 : true}
        lockedUntil={lockedUntil}
        truncated={totals?.truncated ?? false}
        onAddPixKey={() => {
          void navigate({ to: '/app/settings/pix-keys' });
        }}
      />

      {pixKeys.error !== null && (
        <ErrorState
          error={pixKeys.error}
          onRetry={() => {
            void pixKeys.refetch();
          }}
        />
      )}

      {/* A soma do mês tem erro PRÓPRIO: sem isto um 400 viraria "R$ 0,00" em três KPIs
          e no "usado hoje" do limite, sem ninguém saber. */}
      {month.error !== null && (
        <ErrorState
          error={month.error}
          onRetry={() => {
            void month.refetch();
          }}
        />
      )}

      <WithdrawalsList
        rows={rows}
        tab={tab}
        onTabChange={(next) => {
          void navigate({
            to: '/app/withdrawals',
            search: next === 'all' ? {} : { tab: next },
          });
        }}
        counts={counts}
        total={counts[tab]}
        settledCents={totals?.settled ?? 0}
        isLoading={listPending}
        error={primary.error ?? (merged ? rejected.error : null)}
        onRetry={() => {
          void primary.refetch();
          if (merged) {
            void rejected.refetch();
          }
        }}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={() => {
          if (primary.hasNextPage) {
            void primary.fetchNextPage();
          }
          if (merged && rejected.hasNextPage) {
            void rejected.fetchNextPage();
          }
        }}
        onSelect={(withdrawal) => {
          void navigate({
            to: '/app/withdrawals/$id',
            params: { id: withdrawal.id },
            search,
          });
        }}
        {...(detail === undefined ? {} : { selectedId: detail.params.id })}
        hasPixKey={pixKeys.isSuccess ? keys.length > 0 : true}
      />

      <WithdrawalFormDrawer
        open={formOpen}
        onOpenChange={setFormOpen}
        available={available}
        feeCents={feeCents}
        pixKeys={keys}
        submitting={create.isPending}
        error={create.error}
        onSubmit={(body) => {
          create.mutate(body);
        }}
      />

      <RouteDrawer
        open={detail !== undefined}
        onClose={() => {
          void navigate({ to: '/app/withdrawals', search });
        }}
        title="Detalhe do saque"
        {...(detail === undefined
          ? {}
          : { sub: <span className="font-mono">{detail.params.id}</span> })}
      >
        <Outlet />
      </RouteDrawer>
    </>
  );
}

export const Route = createFileRoute('/app/withdrawals')({
  validateSearch: searchSchema,
  component: Withdrawals,
});
